<template>
  <div class="min-h-screen w-full bg-gray-50">
    <div class="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-900">Frontend Events (TEST)</h1>
        <p class="mt-1 text-sm text-gray-600">
          View error and warning events reported by the frontend. TEST tenant only.
        </p>
      </div>

      <div v-if="error" class="mb-4 rounded-md bg-red-50 p-4">
        <p class="text-sm text-red-800">{{ error }}</p>
      </div>

      <div class="mb-4 flex items-center gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700">Filter by level</label>
          <select
            v-model="selectedLevel"
            class="mt-1 block rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            @change="handleFilterChange"
          >
            <option value="all">All</option>
            <option value="error">Errors</option>
            <option value="warning">Warnings</option>
          </select>
        </div>
        <div class="mt-6">
          <button
            type="button"
            class="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            :disabled="loading"
            @click="loadEvents"
          >
            {{ loading ? "Loading..." : "Refresh" }}
          </button>
        </div>
      </div>

      <div v-if="loading && events.length === 0" class="text-center py-12">
        <p class="text-gray-500">Loading events...</p>
      </div>

      <div v-else-if="events.length === 0 && !loading" class="text-center py-12">
        <p class="text-gray-500">No events found.</p>
      </div>

      <div v-else class="bg-white shadow overflow-hidden sm:rounded-md">
        <ul role="list" class="divide-y divide-gray-200">
          <li
            v-for="event in events"
            :key="event._id"
            class="px-6 py-4 hover:bg-gray-50 cursor-pointer"
            @click="selectEvent(event)"
          >
            <div class="flex items-center justify-between">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-3">
                  <span
                    :class="[
                      'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                      event.level === 'error' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                    ]"
                  >
                    {{ event.level }}
                  </span>
                  <span class="text-xs text-gray-500">{{ event.kind }}</span>
                </div>
                <p class="mt-2 text-sm font-medium text-gray-900">{{ event.name }}</p>
                <p class="mt-1 text-sm text-gray-600 ">{{ event.messageNorm }}</p>
                <div class="mt-2 flex items-center gap-4 text-xs text-gray-500">
                  <span>Count: {{ event.count }}</span>
                  <span>Users: {{ event.userCount }}</span>
                  <span>URLs: {{ event.urlCount }}</span>
                  <span>Last seen: {{ formatDate(event.lastSeenAt) }}</span>
                </div>
              </div>
              <div class="ml-4">
                <svg
                  class="h-5 w-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <div v-if="total > events.length" class="mt-4 text-center">
        <p class="text-sm text-gray-500">
          Showing {{ events.length }} of {{ total }} events
        </p>
      </div>
    </div>

    <!-- Event Detail Modal -->
    <div
      v-if="selectedEvent"
      class="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          @click="selectedEvent = null"
        ></div>

        <span class="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>

        <div
          class="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:align-middle"
        >
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mt-3 w-full text-center sm:mt-0 sm:text-left">
                <h3 id="modal-title" class="text-lg font-medium leading-6 text-gray-900">
                  Event Details
                </h3>
                <div class="mt-4 space-y-4">
                  <div>
                    <h4 class="text-sm font-medium text-gray-700">Basic Info</h4>
                    <dl class="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div>
                        <dt class="font-medium text-gray-500">Level</dt>
                        <dd class="mt-1 text-gray-900">{{ selectedEvent.level }}</dd>
                      </div>
                      <div>
                        <dt class="font-medium text-gray-500">Kind</dt>
                        <dd class="mt-1 text-gray-900">{{ selectedEvent.kind }}</dd>
                      </div>
                      <div>
                        <dt class="font-medium text-gray-500">Name</dt>
                        <dd class="mt-1 text-gray-900">{{ selectedEvent.name }}</dd>
                      </div>
                      <div>
                        <dt class="font-medium text-gray-500">Count</dt>
                        <dd class="mt-1 text-gray-900">{{ selectedEvent.count }}</dd>
                      </div>
                      <div>
                        <dt class="font-medium text-gray-500">User Count</dt>
                        <dd class="mt-1 text-gray-900">{{ selectedEvent.userCount }}</dd>
                      </div>
                      <div>
                        <dt class="font-medium text-gray-500">URL Count</dt>
                        <dd class="mt-1 text-gray-900">{{ selectedEvent.urlCount }}</dd>
                      </div>
                      <div>
                        <dt class="font-medium text-gray-500">First Seen</dt>
                        <dd class="mt-1 text-gray-900">{{ formatDate(selectedEvent.firstSeenAt) }}</dd>
                      </div>
                      <div>
                        <dt class="font-medium text-gray-500">Last Seen</dt>
                        <dd class="mt-1 text-gray-900">{{ formatDate(selectedEvent.lastSeenAt) }}</dd>
                      </div>
                    </dl>
                  </div>

                  <div>
                    <h4 class="text-sm font-medium text-gray-700">Message</h4>
                    <p class="mt-2 text-sm text-gray-900 whitespace-pre-wrap">{{ selectedEvent.messageRaw }}</p>
                  </div>

                  <div v-if="selectedEvent.stackRaw">
                    <h4 class="text-sm font-medium text-gray-700">Stack Trace</h4>
                    <pre class="mt-2 text-xs text-gray-900 bg-gray-50 p-3 rounded overflow-x-auto">{{ selectedEvent.stackRaw }}</pre>
                  </div>

                  <div v-if="selectedEvent.urls && selectedEvent.urls.length > 0">
                    <h4 class="text-sm font-medium text-gray-700">URLs ({{ selectedEvent.urls.length }})</h4>
                    <ul class="mt-2 space-y-1">
                      <li v-for="(url, idx) in selectedEvent.urls" :key="idx" class="text-sm text-gray-900">
                        {{ url.path }} <span class="text-gray-500">({{ url.count }})</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 class="text-sm font-medium text-gray-700">Fingerprint</h4>
                    <p class="mt-2 text-xs font-mono text-gray-600 break-all">{{ selectedEvent._id }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
              @click="selectedEvent = null"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { callable, appConfig } from "../../../db.js";

const events = ref([]);
const total = ref(0);
const selectedLevel = ref("all");
const loading = ref(false);
const error = ref("");
const selectedEvent = ref(null);

const searchFrontendEvents = callable("searchFrontendEvents");

async function loadEvents() {
  loading.value = true;
  error.value = "";
  
  try {
    const response = await searchFrontendEvents({
      databaseURL: appConfig?.firebaseConfig?.databaseURL,
      level: selectedLevel.value,
      from: 0,
      size: 50,
    });
    
    events.value = response.data.hits;
    total.value = response.data.total;
  } catch (err) {
    error.value = err?.message || "Failed to load events.";
    console.error("Failed to load frontend events:", err);
  } finally {
    loading.value = false;
  }
}

function handleFilterChange() {
  loadEvents();
}

function selectEvent(event) {
  selectedEvent.value = event;
}

function formatDate(dateString) {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch {
    return dateString;
  }
}

onMounted(() => {
  loadEvents();
});
</script>
